import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, Divider, Link, Stack, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import TopBar from '../../../components/TopBar';
import { formatCurrentPeriod, formatEpochDateTime } from '../../../utils/date';
import { useVendorFeatures } from '../../../hooks/useVendorFeatures';
import { useGetOrganizationHandler } from '../../organizations/dataHandlers/getOrganizationHandler';
import SubscriptionInvoicesTable from '../components/SubscriptionInvoicesTable';
import SubscriptionItems from '../components/SubscriptionItems';
import SubscriptionCurrentUsage from '../components/SubscriptionCurrentUsage';
import { useGetSubscriptionHandler } from '../dataHandlers/getSubscriptionHandler';
import { useGetSubscriptionInvoicesHandler } from '../dataHandlers/getSubscriptionInvoicesHandler';

const getSubscriptionStatusColor = (status) => {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'success';
    case 'past_due':
    case 'unpaid':
    case 'incomplete':
      return 'warning';
    case 'canceled':
    case 'incomplete_expired':
      return 'error';
    default:
      return 'default';
  }
};

export default function SubscriptionDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const [invoicePagination, setInvoicePagination] = useState({ page: 0, size: 10 });
  const [copiedSubscriptionId, setCopiedSubscriptionId] = useState(false);
  const [copiedCustomerId, setCopiedCustomerId] = useState(false);
  const { subscription, isLoading } = useGetSubscriptionHandler(id);
  const { organization, isLoading: isOrganizationLoading } = useGetOrganizationHandler(
    subscription?.stripe_customer_id,
  );
  const { getEnabledFeatureLabels } = useVendorFeatures();
  const enabledFeatureLabels = getEnabledFeatureLabels(organization?.features);
  const {
    invoices,
    totalDocuments,
    isLoading: isInvoicesLoading,
  } = useGetSubscriptionInvoicesHandler({
    searchQuery: {
      limit: invoicePagination.size,
      offset: invoicePagination.page * invoicePagination.size,
      stripe_subscription_id: id,
    },
  });

  const formatDateTime = (value) => {
    if (value === null || value === undefined || value === '') return 'NA';

    const epochSeconds = Number(value);
    if (Number.isNaN(epochSeconds)) return 'NA';

    const parsedDate = new Date(epochSeconds * 1000);
    if (Number.isNaN(parsedDate.getTime())) return 'NA';

    return parsedDate.toLocaleString();
  };

  const handleCopySubscriptionId = async () => {
    if (!subscription?.stripe_subscription_id) return;

    try {
      await navigator.clipboard.writeText(subscription.stripe_subscription_id);
      setCopiedSubscriptionId(true);

      setTimeout(() => {
        setCopiedSubscriptionId(false);
      }, 1500);
    } catch (error) {
      setCopiedSubscriptionId(false);
    }
  };

  const handleCopyCustomerId = async () => {
    if (!subscription?.stripe_customer_id) return;

    try {
      await navigator.clipboard.writeText(subscription.stripe_customer_id);
      setCopiedCustomerId(true);

      setTimeout(() => {
        setCopiedCustomerId(false);
      }, 1500);
    } catch (error) {
      setCopiedCustomerId(false);
    }
  };

  const subscriptionStatus = subscription?.status || 'NA';

  const detailRows = [
    { label: 'Status', value: subscriptionStatus, chip: true },
    { label: 'Payment Buffer', value: (subscription?.due_after ?? 0) + ' day(s)' },
    { label: 'Created on', value: formatEpochDateTime(subscription?.created_at, { emptyValue: 'NA' }) },
    {
      label: 'Current Period',
      value: formatCurrentPeriod(subscription?.current_period_start, subscription?.current_period_end, {
        emptyValue: 'NA',
      }),
    },
  ];

  function onClickingBackButton() {
    return navigate('/subscriptions');
  }

  if (isLoading || !subscription) return <CircularProgress />;

  return (
    <Stack spacing={2} sx={{ mt: '-1rem' }}>
      <Stack spacing={2.5}>
        <TopBar title="Subscription Details">
          <Button
            data-testid="subscription-profile-back-button"
            key="back"
            variant="contained"
            color="primary"
            onClick={onClickingBackButton}
            className="action-button"
            sx={{ marginLeft: theme.spacing(1) }}
          >
            Back
          </Button>
        </TopBar>
        <Box>
          <Divider sx={{ mb: 1 }} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={600}>
                  Subscription ID:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {subscription?.stripe_subscription_id || 'NA'}
                </Typography>
                {subscription?.stripe_subscription_id ? (
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    variant="body2"
                    onClick={handleCopySubscriptionId}
                  >
                    {copiedSubscriptionId ? 'Copied' : 'Copy'}
                  </Link>
                ) : null}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={600}>
                  Customer ID:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {subscription?.stripe_customer_id || 'NA'}
                </Typography>
                {subscription?.stripe_customer_id ? (
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    variant="body2"
                    onClick={handleCopyCustomerId}
                  >
                    {copiedCustomerId ? 'Copied' : 'Copy'}
                  </Link>
                ) : null}
              </Stack>
            </Grid>
            {detailRows.map((row) => (
              <Grid key={row.label} size={{ xs: 12, md: 6 }}>
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2" fontWeight={600}>
                    {row.label}:
                  </Typography>
                  {row.chip ? (
                    <Chip label={row.value} size="small" color={getSubscriptionStatusColor(subscription?.status)} />
                  ) : row.href ? (
                    <Link href={row.href} underline="hover" variant="body2">
                      {row.value}
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {row.value}
                    </Typography>
                  )}
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Organization Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {isOrganizationLoading ? (
            <CircularProgress size={24} />
          ) : (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Name:
                  </Typography>
                  {organization?.name ? (
                    <Link
                      component={RouterLink}
                      to={`/organizations/${subscription.stripe_customer_id}`}
                      underline="hover"
                      variant="body2"
                    >
                      {organization.name}
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      NA
                    </Typography>
                  )}
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Email:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {organization?.email || 'NA'}
                  </Typography>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Stack spacing={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Enabled Features:
                  </Typography>
                  {enabledFeatureLabels.length > 0 ? (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {enabledFeatureLabels.map((label) => (
                        <Chip key={label} label={label} size="small" color="primary" variant="outlined" />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      None
                    </Typography>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}
        </Box>

        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Cancellation Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Expiry:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDateTime(subscription?.canceled_at || subscription?.cancel_at || subscription?.ended_at)}
            </Typography>
          </Stack>
        </Box>

        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Current Usage Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <SubscriptionCurrentUsage
            subscriptionId={id}
            periodStart={subscription?.current_period_start}
            periodEnd={subscription?.current_period_end}
          />
        </Box>

        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Item Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <SubscriptionItems readOnly items={subscription?.items || []} errors={{}} />
        </Box>

        <Box>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Invoices
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <SubscriptionInvoicesTable
            subscriptionId={id}
            invoices={invoices}
            totalDocuments={totalDocuments}
            isLoading={isInvoicesLoading}
            handlePaginationChange={(page, limit) => setInvoicePagination({ page, size: limit })}
          />
        </Box>
      </Stack>
    </Stack>
  );
}
